import {
  Badge,
  Button,
  DataGridFilter,
  type FilterConfig,
  FormInputField,
  ToggleGroup,
  ToggleGroupItem,
} from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import type { Table } from "@tanstack/react-table";
import { ListFilter, Search } from "lucide-react";
import { useMemo } from "react";
import type { Control } from "react-hook-form";
import { HOME_APPLICATION_FILTER_ALL } from "../homeLaunchpadFilters.utils";

export interface IHomeLaunchpadToolbarFormValues {
  searchQuery: string;
}

interface IHomeLaunchpadToolbarProps {
  control: Control<IHomeLaunchpadToolbarFormValues>;
  selectedApplicationNames: string[];
  onApplicationFilterChange: (applicationName: string) => void;
  availableApplicationNames: string[];
  filterTable: Table<{ id: string }>;
  filterConfig: FilterConfig[];
  activeFilterCount: number;
}

export function HomeLaunchpadToolbar({
  control,
  selectedApplicationNames,
  onApplicationFilterChange,
  availableApplicationNames,
  filterTable,
  filterConfig,
  activeFilterCount,
}: IHomeLaunchpadToolbarProps) {
  const { t } = useTranslation();

  const visibleApplicationFilters = useMemo(
    () => [
      {
        id: HOME_APPLICATION_FILTER_ALL,
        label: t("hub.home.filter_all", { defaultValue: "All" }),
      },
      ...availableApplicationNames.map((applicationName) => ({
        id: applicationName,
        label: applicationName,
      })),
    ],
    [availableApplicationNames, t],
  );

  const selectedApplicationValue =
    selectedApplicationNames[0] ?? HOME_APPLICATION_FILTER_ALL;

  return (
    <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
      <div className="flex min-w-0 flex-1 flex-nowrap items-center">
        <ToggleGroup
          type="single"
          groupVariant="withSpacing"
          size="sm"
          variant="outline"
          value={selectedApplicationValue}
          onValueChange={(value: string) => {
            onApplicationFilterChange(value || HOME_APPLICATION_FILTER_ALL);
          }}
          className="flex-nowrap">
          {visibleApplicationFilters.map((filter) => (
            <ToggleGroupItem key={filter.id} value={filter.id} className="mr-2">
              {filter.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      <div className="flex w-full shrink-0 flex-col gap-3 sm:w-auto sm:flex-row sm:items-center sm:justify-end">
        <DataGridFilter
          table={filterTable}
          filters={filterConfig}
          trigger={
            <Button variant="outline" size="sm" className="h-9">
              <ListFilter className="h-4 w-4" />
              {t("hub.home.filter_by_placeholder", {
                defaultValue: "Filter by",
              })}
              {activeFilterCount > 0 ? (
                <Badge
                  appearance="default"
                  shape="circle"
                  size="xs"
                  variant="secondary">
                  {activeFilterCount}
                </Badge>
              ) : null}
            </Button>
          }
        />
        <div className="w-full sm:w-70 sm:max-w-70 pr-3">
          <FormInputField
            control={control}
            name="searchQuery"
            type="search"
            addonStart={<Search className="h-4 w-4 text-muted-foreground" />}
            placeholder={t("hub.home.search_placeholder", {
              defaultValue: "Search...",
            })}
          />
        </div>
      </div>
    </div>
  );
}
